import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  FileText,
  MoreVertical,
  Eye,
  Copy,
  Download,
  Trash2,
  Library,
  Calendar,
  HelpCircle,
  GraduationCap,
  Folder,
  FolderPlus,
  FolderOpen,
  Edit2,
  ChevronRight,
  X,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuizLibraryPage = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const [quizzes, setQuizzes] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(null); // null = all quizzes
  
  // Folder modal state
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [folderModalMode, setFolderModalMode] = useState('create'); // 'create' or 'edit'
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [savingFolder, setSavingFolder] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [quizzesRes, foldersRes] = await Promise.all([
        axios.get(`${API}/quizzes`, { headers: getAuthHeader() }),
        axios.get(`${API}/folders`, { headers: getAuthHeader() })
      ]);
      setQuizzes(quizzesRes.data);
      setFolders(foldersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load library');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (quizId) => {
    if (!confirm('Are you sure you want to delete this quiz?')) return;

    setDeletingId(quizId);
    try {
      await axios.delete(`${API}/quizzes/${quizId}`, {
        headers: getAuthHeader()
      });
      setQuizzes(quizzes.filter(q => q.id !== quizId));
      toast.success('Quiz deleted');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete quiz');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDuplicate = async (quizId) => {
    try {
      const response = await axios.post(
        `${API}/quizzes/${quizId}/duplicate`,
        {},
        { headers: getAuthHeader() }
      );
      setQuizzes([response.data, ...quizzes]);
      toast.success('Quiz duplicated');
    } catch (error) {
      console.error('Duplicate error:', error);
      toast.error('Failed to duplicate quiz');
    }
  };

  const handleExport = async (quizId, format) => {
    try {
      const response = await axios.get(
        `${API}/quizzes/${quizId}/export/${format}?include_answers=true`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );

      const quiz = quizzes.find(q => q.id === quizId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${quiz.title.replace(/\s+/g, '_')}_quiz.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export quiz');
    }
  };

  const handleMoveToFolder = async (quizId, folderId) => {
    try {
      await axios.put(
        `${API}/quizzes/${quizId}/folder`,
        null,
        { 
          headers: getAuthHeader(),
          params: { folder_id: folderId }
        }
      );
      
      // Update local state
      setQuizzes(quizzes.map(q => 
        q.id === quizId ? { ...q, folder_id: folderId } : q
      ));
      
      // Update folder counts
      const updatedFolders = folders.map(f => ({
        ...f,
        quiz_count: quizzes.filter(q => 
          q.id === quizId ? folderId === f.id : q.folder_id === f.id
        ).length
      }));
      setFolders(updatedFolders);
      
      const folderName = folderId ? folders.find(f => f.id === folderId)?.name : 'No Folder';
      toast.success(`Moved to ${folderName}`);
    } catch (error) {
      console.error('Move error:', error);
      toast.error('Failed to move quiz');
    }
  };

  const handleCreateFolder = () => {
    setFolderModalMode('create');
    setFolderName('');
    setEditingFolder(null);
    setShowFolderModal(true);
  };

  const handleEditFolder = (folder) => {
    setFolderModalMode('edit');
    setFolderName(folder.name);
    setEditingFolder(folder);
    setShowFolderModal(true);
  };

  const handleDeleteFolder = async (folderId) => {
    const folder = folders.find(f => f.id === folderId);
    if (!confirm(`Delete "${folder.name}"? Quizzes in this folder will be moved to "All Quizzes".`)) return;

    try {
      await axios.delete(`${API}/folders/${folderId}`, {
        headers: getAuthHeader()
      });
      
      // Update local state
      setFolders(folders.filter(f => f.id !== folderId));
      setQuizzes(quizzes.map(q => 
        q.folder_id === folderId ? { ...q, folder_id: null } : q
      ));
      
      if (selectedFolderId === folderId) {
        setSelectedFolderId(null);
      }
      
      toast.success('Folder deleted');
    } catch (error) {
      console.error('Delete folder error:', error);
      toast.error('Failed to delete folder');
    }
  };

  const handleSaveFolder = async () => {
    if (!folderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }

    setSavingFolder(true);
    try {
      if (folderModalMode === 'create') {
        const response = await axios.post(
          `${API}/folders`,
          { name: folderName.trim() },
          { headers: getAuthHeader() }
        );
        setFolders([...folders, response.data].sort((a, b) => a.name.localeCompare(b.name)));
        toast.success('Folder created');
      } else {
        await axios.put(
          `${API}/folders/${editingFolder.id}`,
          { name: folderName.trim() },
          { headers: getAuthHeader() }
        );
        setFolders(folders.map(f => 
          f.id === editingFolder.id ? { ...f, name: folderName.trim() } : f
        ).sort((a, b) => a.name.localeCompare(b.name)));
        toast.success('Folder renamed');
      }
      setShowFolderModal(false);
    } catch (error) {
      console.error('Save folder error:', error);
      toast.error('Failed to save folder');
    } finally {
      setSavingFolder(false);
    }
  };

  // Filter quizzes based on search and selected folder
  const filteredQuizzes = quizzes.filter(quiz => {
    const matchesSearch = 
      quiz.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quiz.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quiz.grade_level.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFolder = selectedFolderId === null 
      ? true  // Show all quizzes
      : selectedFolderId === 'unfiled'
        ? !quiz.folder_id  // Show quizzes with no folder
        : quiz.folder_id === selectedFolderId;  // Show quizzes in selected folder
    
    return matchesSearch && matchesFolder;
  });

  const unfiledCount = quizzes.filter(q => !q.folder_id).length;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8"
        >
          <div>
            <h1 className="font-serif text-3xl md:text-4xl font-medium text-primary mb-2">
              Quiz Library
            </h1>
            <p className="text-muted-foreground">
              {quizzes.length} {quizzes.length === 1 ? 'quiz' : 'quizzes'} saved
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCreateFolder}
              className="gap-2"
              data-testid="create-folder-btn"
            >
              <FolderPlus className="w-4 h-4" />
              New Folder
            </Button>
            <Button
              onClick={() => navigate('/create')}
              className="gap-2 bg-primary hover:bg-primary/90"
              data-testid="create-new-btn"
            >
              <Plus className="w-4 h-4" />
              Create Quiz
            </Button>
          </div>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Folder Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:w-64 flex-shrink-0"
          >
            <Card className="border-border sticky top-24">
              <CardContent className="p-4">
                <h3 className="font-medium text-sm text-muted-foreground mb-3 uppercase tracking-wide">
                  Folders
                </h3>
                <div className="space-y-1">
                  {/* All Quizzes */}
                  <button
                    onClick={() => setSelectedFolderId(null)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedFolderId === null
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'hover:bg-secondary text-foreground'
                    }`}
                    data-testid="folder-all"
                  >
                    <div className="flex items-center gap-2">
                      <Library className="w-4 h-4" />
                      <span>All Quizzes</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {quizzes.length}
                    </Badge>
                  </button>

                  {/* Unfiled */}
                  <button
                    onClick={() => setSelectedFolderId('unfiled')}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedFolderId === 'unfiled'
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'hover:bg-secondary text-foreground'
                    }`}
                    data-testid="folder-unfiled"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      <span>Unfiled</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {unfiledCount}
                    </Badge>
                  </button>

                  {/* Divider */}
                  {folders.length > 0 && (
                    <div className="border-t border-border my-2" />
                  )}

                  {/* Custom Folders */}
                  {folders.map((folder) => (
                    <div
                      key={folder.id}
                      className={`group flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedFolderId === folder.id
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'hover:bg-secondary text-foreground'
                      }`}
                    >
                      <button
                        onClick={() => setSelectedFolderId(folder.id)}
                        className="flex items-center gap-2 flex-1 min-w-0"
                        data-testid={`folder-${folder.id}`}
                      >
                        {selectedFolderId === folder.id ? (
                          <FolderOpen className="w-4 h-4 flex-shrink-0" />
                        ) : (
                          <Folder className="w-4 h-4 flex-shrink-0" />
                        )}
                        <span className="truncate">{folder.name}</span>
                      </button>
                      <div className="flex items-center gap-1">
                        <Badge variant="secondary" className="text-xs">
                          {folder.quiz_count}
                        </Badge>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <MoreVertical className="w-3 h-3" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEditFolder(folder)}>
                              <Edit2 className="w-4 h-4 mr-2" />
                              Rename
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDeleteFolder(folder.id)}
                              className="text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Search */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mb-6"
            >
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="Search quizzes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12"
                  data-testid="search-input"
                />
              </div>
            </motion.div>

            {/* Current Folder Header */}
            {selectedFolderId && selectedFolderId !== 'unfiled' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex items-center gap-2 mb-4 text-sm text-muted-foreground"
              >
                <FolderOpen className="w-4 h-4" />
                <span>{folders.find(f => f.id === selectedFolderId)?.name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2"
                  onClick={() => setSelectedFolderId(null)}
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear
                </Button>
              </motion.div>
            )}

            {/* Quiz Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Card key={i} className="border-border">
                    <CardContent className="p-6">
                      <Skeleton className="h-6 w-3/4 mb-3" />
                      <Skeleton className="h-4 w-1/2 mb-4" />
                      <Skeleton className="h-4 w-1/3" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredQuizzes.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                <AnimatePresence>
                  {filteredQuizzes.map((quiz, index) => (
                    <motion.div
                      key={quiz.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card
                        className={`border-border hover:border-primary/30 hover:shadow-lg transition-all group cursor-pointer ${
                          deletingId === quiz.id ? 'opacity-50' : ''
                        }`}
                        data-testid={`quiz-card-${index}`}
                      >
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <Link to={`/quiz/${quiz.id}`} className="flex-1 min-w-0">
                              <h3 className="font-serif text-lg font-medium text-primary truncate group-hover:text-accent transition-colors">
                                {quiz.title}
                              </h3>
                            </Link>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="flex-shrink-0 -mr-2"
                                  data-testid={`quiz-menu-${index}`}
                                >
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/quiz/${quiz.id}`)}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  View & Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDuplicate(quiz.id)}>
                                  <Copy className="w-4 h-4 mr-2" />
                                  Duplicate
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {/* Move to Folder Submenu */}
                                <DropdownMenuSub>
                                  <DropdownMenuSubTrigger>
                                    <Folder className="w-4 h-4 mr-2" />
                                    Move to Folder
                                  </DropdownMenuSubTrigger>
                                  <DropdownMenuSubContent>
                                    <DropdownMenuItem
                                      onClick={() => handleMoveToFolder(quiz.id, null)}
                                      disabled={!quiz.folder_id}
                                    >
                                      <FileText className="w-4 h-4 mr-2" />
                                      Unfiled
                                      {!quiz.folder_id && <span className="ml-auto text-xs text-muted-foreground">Current</span>}
                                    </DropdownMenuItem>
                                    {folders.length > 0 && <DropdownMenuSeparator />}
                                    {folders.map(folder => (
                                      <DropdownMenuItem
                                        key={folder.id}
                                        onClick={() => handleMoveToFolder(quiz.id, folder.id)}
                                        disabled={quiz.folder_id === folder.id}
                                      >
                                        <Folder className="w-4 h-4 mr-2" />
                                        {folder.name}
                                        {quiz.folder_id === folder.id && <span className="ml-auto text-xs text-muted-foreground">Current</span>}
                                      </DropdownMenuItem>
                                    ))}
                                    {folders.length === 0 && (
                                      <DropdownMenuItem disabled className="text-muted-foreground">
                                        No folders yet
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuSubContent>
                                </DropdownMenuSub>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleExport(quiz.id, 'pdf')}>
                                  <Download className="w-4 h-4 mr-2" />
                                  Export as PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleExport(quiz.id, 'docx')}>
                                  <Download className="w-4 h-4 mr-2" />
                                  Export as DOCX
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleDelete(quiz.id)}
                                  className="text-destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>

                          <Link to={`/quiz/${quiz.id}`} className="block">
                            {/* Folder badge */}
                            {quiz.folder_id && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-2">
                                <Folder className="w-3 h-3" />
                                <span>{folders.find(f => f.id === quiz.folder_id)?.name}</span>
                              </div>
                            )}

                            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                              <FileText className="w-4 h-4" />
                              <span>{quiz.subject}</span>
                              <span>•</span>
                              <span>{quiz.grade_level}</span>
                              {quiz.is_practice && (
                                <Badge variant="outline" className="ml-1 text-[10px] px-1.5 py-0 border-primary/40 text-primary" data-testid={`quiz-practice-badge-${quiz.id}`}>
                                  Practice
                                </Badge>
                              )}
                            </div>

                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <HelpCircle className="w-4 h-4" />
                                <span>{quiz.questions.length} questions</span>
                              </div>
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Calendar className="w-4 h-4" />
                                <span>{formatDate(quiz.updated_at)}</span>
                              </div>
                            </div>

                            {/* Standards badges */}
                            {quiz.standards && quiz.standards.length > 0 && (
                              <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-border">
                                <GraduationCap className="w-3.5 h-3.5 text-primary" />
                                <div className="flex gap-1 flex-wrap">
                                  {quiz.standards.slice(0, 2).map(code => (
                                    <Badge key={code} variant="outline" className="text-[10px] px-1.5 py-0">
                                      {code.split('.').slice(-2).join('.')}
                                    </Badge>
                                  ))}
                                  {quiz.standards.length > 2 && (
                                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                                      +{quiz.standards.length - 2}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            )}
                          </Link>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-16"
              >
                <div className="w-20 h-20 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                  <Library className="w-10 h-10 text-muted-foreground" />
                </div>
                {searchQuery ? (
                  <>
                    <h3 className="font-serif text-xl font-medium text-primary mb-2">
                      No quizzes found
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Try a different search term
                    </p>
                    <Button variant="outline" onClick={() => setSearchQuery('')}>
                      Clear Search
                    </Button>
                  </>
                ) : selectedFolderId ? (
                  <>
                    <h3 className="font-serif text-xl font-medium text-primary mb-2">
                      This folder is empty
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Move quizzes here using the menu on each quiz card
                    </p>
                    <Button variant="outline" onClick={() => setSelectedFolderId(null)}>
                      View All Quizzes
                    </Button>
                  </>
                ) : (
                  <>
                    <h3 className="font-serif text-xl font-medium text-primary mb-2">
                      Your library is empty
                    </h3>
                    <p className="text-muted-foreground mb-6">
                      Create your first quiz to get started
                    </p>
                    <Button
                      onClick={() => navigate('/create')}
                      className="gap-2 bg-primary hover:bg-primary/90"
                      data-testid="empty-create-btn"
                    >
                      <Plus className="w-4 h-4" />
                      Create Your First Quiz
                    </Button>
                  </>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </main>

      {/* Folder Create/Edit Modal */}
      <Dialog open={showFolderModal} onOpenChange={setShowFolderModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">
              {folderModalMode === 'create' ? 'Create New Folder' : 'Rename Folder'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Folder name"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveFolder()}
              autoFocus
              data-testid="folder-name-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFolderModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveFolder} disabled={savingFolder}>
              {savingFolder && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {folderModalMode === 'create' ? 'Create' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuizLibraryPage;
